import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { ArrowRight } from "lucide-react";
import { BarChart, DonutChart, LineChart } from "@mantine/charts";
import { useMemo } from "react";
import { formatBucketChartLabel } from "@mip-tg-bot-turntable/utils";
import type { AnalyticsShortlinkDisposition } from "../../../api.ts";
import { adminErrText, AdminApiError } from "../../../api.ts";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { granularityLabel, trendRangeLabel } from "../../../components/StatsTrendControls.tsx";
import { AnalyticsStatsFilters } from "./AnalyticsStatsFilters.tsx";
import { StatsKpiCard } from "./StatsKpiCard.tsx";
import {
  CONV_STATUS_COLORS,
  CONV_STATUS_LABELS,
  csvCell,
  DISPOSITION_COLORS,
  DISPOSITION_LABELS,
  fmtPct,
} from "./stats-shared.ts";
import { OVERVIEW_SECTIONS, useAnalyticsStats } from "./useAnalyticsStats.ts";
import type { UntrackedJoinsNavFilters } from "./untracked-join-shared.ts";

type Props = {
  readonly botId: string;
  readonly onOpenUntrackedJoins?: (filters?: UntrackedJoinsNavFilters) => void;
  readonly onOpenTab?: (tab: string) => void;
};

export function AnalyticsOverviewTab({ botId, onOpenUntrackedJoins, onOpenTab }: Props) {
  const stats = useAnalyticsStats(botId, OVERVIEW_SECTIONS);
  const { data, isLoading, isError, error, activeRange, activeGranularity } = stats;
  const overview = data?.overview;
  const opsAlerts = data?.opsAlerts;

  const withBucketLabel = <T extends { readonly bucket: string }>(
    items: ReadonlyArray<T>,
  ): Array<T & { readonly bucketLabel: string }> =>
    items.map((it) => ({
      ...it,
      bucketLabel: formatBucketChartLabel(it.bucket, activeGranularity),
    }));

  const dispositionDonut = useMemo(
    () =>
      (data?.dispositionBreakdown ?? []).map((it) => ({
        name: DISPOSITION_LABELS[it.disposition as AnalyticsShortlinkDisposition] ?? it.disposition,
        value: it.count,
        color: DISPOSITION_COLORS[it.disposition as AnalyticsShortlinkDisposition] ?? "gray.6",
      })),
    [data?.dispositionBreakdown],
  );

  const conversionDonut = useMemo(
    () =>
      (data?.conversionStatus ?? []).map((it) => ({
        name: CONV_STATUS_LABELS[it.status] ?? it.status,
        value: it.count,
        color: CONV_STATUS_COLORS[it.status] ?? "gray.6",
      })),
    [data?.conversionStatus],
  );

  const untrackedTotal = useMemo(
    () => (data?.untrackedJoinBreakdown ?? []).reduce((acc, it) => acc + it.count, 0),
    [data?.untrackedJoinBreakdown],
  );

  const chatCount = data?.funnelTrendByChat?.length ?? 0;

  const handleExport = () => {
    if (data === undefined) return;
    const lines: string[] = [];
    lines.push("概览");
    lines.push("指标,值");
    lines.push(`短链访问(事件),${csvCell(data.overview.totalVisits)}`);
    lines.push(`正常归因访问(事件),${csvCell(data.overview.attributedVisits)}`);
    lines.push(`Cookie复用(事件),${csvCell(data.overview.cookieReuseVisits)}`);
    lines.push(`归因入群(事件),${csvCell(data.overview.joinEvents ?? data.overview.joinedClicks)}`);
    lines.push(`入群用户(去重),${csvCell(data.overview.joinUsers ?? "")}`);
    lines.push(`已上报(事件),${csvCell(data.overview.conversionsSent)}`);
    lines.push(`上报跳过(事件),${csvCell(data.overview.conversionsSkipped)}`);
    lines.push(`跳过率,${csvCell(fmtPct(data.overview.skipRatePct ?? null))}`);
    lines.push(`归因入群率,${csvCell(fmtPct(data.overview.joinRatePct))}`);
    lines.push(`入群上报率,${csvCell(fmtPct(data.overview.reportRatePct))}`);
    lines.push("");
    lines.push("处置分布");
    lines.push("处置,次数");
    for (const it of data.dispositionBreakdown) {
      const label =
        DISPOSITION_LABELS[it.disposition as AnalyticsShortlinkDisposition] ?? it.disposition;
      lines.push(`${csvCell(label)},${csvCell(it.count)}`);
    }
    lines.push("");
    lines.push(
      `${trendRangeLabel(data.range)}漏斗趋势汇总（按${granularityLabel(data.granularity)}）`,
    );
    lines.push("时间桶,访问,入群,上报,跳过");
    for (const it of data.funnelTrend) {
      lines.push(
        `${csvCell(it.bucket)},${csvCell(it.visits)},${csvCell(it.joined)},${csvCell(it.sent)},${csvCell(it.skipped ?? 0)}`,
      );
    }
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([bom + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analytics-overview-${data.statsDay}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack gap="md">
      {data !== undefined ? (
        <BotTimezoneNotice
          timezone={data.timezone}
          statsDay={data.statsDay}
          range={data.range}
          windowStartAt={data.windowStartAt}
          windowEndAt={data.windowEndAt}
        />
      ) : null}
      {opsAlerts !== undefined && opsAlerts.alerts.length > 0 ? (
        <Stack gap="xs">
          <Group gap="xs">
            <Text fw={600} size="sm">
              运营告警
            </Text>
            <Badge color="red" size="sm">
              {opsAlerts.alerts.length}
            </Badge>
            <Text size="xs" c="dimmed">
              当前筛选时间窗内；点「去处理」跳转对应页
            </Text>
          </Group>
          {opsAlerts.alerts.map((a) => (
            <Alert
              key={a.code + String(a.count)}
              color={a.level === "error" ? "red" : a.level === "warning" ? "orange" : "blue"}
              title={a.title}
            >
              <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
                <Text size="sm" style={{ flex: 1, minWidth: 200 }}>
                  {a.hint}
                </Text>
                {onOpenTab !== undefined ? (
                  <Button
                    size="xs"
                    variant="light"
                    rightSection={<ArrowRight size={14} />}
                    onClick={() => onOpenTab(a.tabHint)}
                  >
                    去处理
                  </Button>
                ) : null}
              </Group>
            </Alert>
          ))}
          {opsAlerts.failedByReason.length > 0 ? (
            <Card withBorder padding="sm" radius="md">
              <Text size="xs" fw={600} mb={6}>
                上报失败原因分布
              </Text>
              <Stack gap={4}>
                {opsAlerts.failedByReason.slice(0, 8).map((r) => (
                  <Group key={r.reason} justify="space-between" gap="xs" wrap="nowrap">
                    <Text size="xs" c="red" lineClamp={2} style={{ flex: 1 }}>
                      {r.reason}
                    </Text>
                    <Badge size="sm" color="red" variant="light">
                      {r.count}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            </Card>
          ) : null}
        </Stack>
      ) : null}
      <AnalyticsStatsFilters
        stats={stats}
        onExport={handleExport}
        exportDisabled={data === undefined}
      />

      {isLoading ? (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} spacing="md">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} h={80} radius="md" />
            ))}
          </SimpleGrid>
          <Skeleton h={280} radius="md" />
        </Stack>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrText((error as AdminApiError).code)}
        </Text>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} spacing="md">
            {overview !== undefined ? (
              <>
                <StatsKpiCard label="短链访问(事件)" value={overview.totalVisits} />
                <StatsKpiCard label="正常归因访问(事件)" value={overview.attributedVisits} />
                <StatsKpiCard label="Cookie复用(事件)" value={overview.cookieReuseVisits} />
                <StatsKpiCard
                  label="归因入群(事件)"
                  value={overview.joinEvents ?? overview.joinedClicks}
                />
                <StatsKpiCard label="入群用户(去重)" value={overview.joinUsers ?? "-"} />
                <StatsKpiCard label="已上报(事件)" value={overview.conversionsSent} />
                <StatsKpiCard label="待上报" value={overview.conversionsPending} />
                <StatsKpiCard label="上报失败" value={overview.conversionsFailed} />
                <StatsKpiCard label="上报跳过(事件)" value={overview.conversionsSkipped} />
                <StatsKpiCard label="跳过率" value={fmtPct(overview.skipRatePct ?? null)} />
                <StatsKpiCard label="归因入群率" value={fmtPct(overview.joinRatePct)} />
                <StatsKpiCard label="入群上报率" value={fmtPct(overview.reportRatePct)} />
                <StatsKpiCard label="未归因入群(事件)" value={untrackedTotal} />
                <StatsKpiCard label="监控群组数" value={chatCount} />
              </>
            ) : null}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} size="lg" mb="md">
                访问处置分布
              </Text>
              {dispositionDonut.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  暂无数据
                </Text>
              ) : (
                <DonutChart
                  data={dispositionDonut}
                  withTooltip
                  tooltipDataSource="segment"
                  size={200}
                  thickness={24}
                  chartLabel={`${overview?.totalVisits.toLocaleString() ?? 0}`}
                />
              )}
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} size="lg" mb="md">
                转化上报状态
              </Text>
              {conversionDonut.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="lg">
                  暂无转化
                </Text>
              ) : (
                <DonutChart
                  data={conversionDonut}
                  withTooltip
                  tooltipDataSource="segment"
                  size={200}
                  thickness={24}
                />
              )}
            </Card>
          </SimpleGrid>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              {trendRangeLabel(activeRange)}访问处置趋势（按{granularityLabel(activeGranularity)}）
            </Text>
            {(data?.visitTrend.length ?? 0) === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无趋势数据
              </Text>
            ) : (
              <LineChart
                h={280}
                data={withBucketLabel(data?.visitTrend ?? [])}
                dataKey="bucketLabel"
                withTooltip
                withLegend
                legendProps={{ verticalAlign: "bottom", height: 40 }}
                tickLine="y"
                curveType="monotone"
                strokeWidth={2}
                dotProps={{ r: activeGranularity === "hour" ? 0 : 3 }}
                activeDotProps={{ r: 4, strokeWidth: 1 }}
                series={[
                  { name: "attributed", label: "正常归因", color: "green.6" },
                  { name: "cookieReuse", label: "Cookie复用", color: "blue.6" },
                  { name: "nonHuman", label: "非真人", color: "gray.6" },
                  { name: "rateLimited", label: "限流跳过", color: "orange.6" },
                  { name: "sharedFallback", label: "共享兜底", color: "yellow.6" },
                  { name: "perAdPrimary", label: "Per-ad主链", color: "cyan.6" },
                  { name: "perAdFallback", label: "Per-ad降级", color: "teal.6" },
                  { name: "naturalFallback", label: "自然兜底", color: "gray.5" },
                  { name: "macroInvalid", label: "宏未替换", color: "red.6" },
                ]}
              />
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb={4}>
              {trendRangeLabel(activeRange)}漏斗趋势 · 全群汇总（按
              {granularityLabel(activeGranularity)}）
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              所有群合在一起的访问 / 入群 / 上报。分群对比见左侧「群组漏斗」。
            </Text>
            {(data?.funnelTrend.length ?? 0) === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无趋势数据
              </Text>
            ) : (
              <BarChart
                h={280}
                data={withBucketLabel(data?.funnelTrend ?? [])}
                dataKey="bucketLabel"
                withTooltip
                withLegend
                legendProps={{ verticalAlign: "bottom", height: 36 }}
                tickLine="y"
                series={[
                  { name: "visits", label: "访问", color: "violet.6" },
                  { name: "joined", label: "入群", color: "teal.6" },
                  { name: "sent", label: "已上报", color: "green.6" },
                  { name: "skipped", label: "跳过", color: "gray.6" },
                ]}
              />
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs" wrap="wrap">
              <Stack gap={2}>
                <Text fw={600} size="lg">
                  未归因入群
                </Text>
                <Text size="xs" c="dimmed">
                  本时段合计 {untrackedTotal.toLocaleString()}
                  。明细与链接 top 见诊断区「未归因入群」。
                </Text>
              </Stack>
              {onOpenUntrackedJoins !== undefined ? (
                <Button
                  variant="light"
                  size="xs"
                  rightSection={<ArrowRight size={14} />}
                  onClick={() => onOpenUntrackedJoins()}
                >
                  查看明细
                </Button>
              ) : null}
            </Group>
          </Card>
        </>
      )}
    </Stack>
  );
}
